import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Textarea } from '@/shared/components/ui/textarea';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import { MoneyInput } from '@/features/ventas/components/MoneyInput';
import type { MedioDePagoItem } from '@/shared/api/controllers/mediosDePago';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { useSession } from '@/shared/auth/SessionProvider';
import { crearTransferencia, getCajaSaldo } from '../api';
import { canWriteCaja } from '../lib/permissions';
import { LlevaFacturaTag } from './LlevaFacturaTag';
import { CajaNombre } from './CajaNombre';

/**
 * Transferencia / Depósito dialog. Mirrors PowerApps
 * Screen_Tesoreria.pa.yaml PopUp_TransferirEntreCuentas (1946) +
 * PopUp_DepositoEntreCuentas (3327) — functionally identical flows
 * that only differ in `Categoria_MC` and auto-tax behavior when the
 * destination is Galicia ARS.
 *
 * On submit the backend creates two paired rows under a shared
 * `IDOperacion_MC`:
 *   - Egreso on `origen` (deducts the caja's balance).
 *   - Ingreso on `destino` (adds to the caja's balance).
 * Both default to `Status_MC = 'Pendiente'` so they show up in the
 * active period; the next Cierre de Caja bumps them to Confirmado.
 *
 * Cross-currency transfers reveal Tipo de cambio + Monto en destino;
 * same-currency hide them.
 */

export function TransferenciaDialog({
  kind,
  origen,
  origenCurrency,
  medios,
  onClose,
}: {
  kind: 'Transferencia' | 'Deposito';
  /** Locked to the caja currently selected in the page toolbar. */
  origen: string;
  origenCurrency: 'ARS' | 'MXN' | 'USD';
  medios: MedioDePagoItem[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { config: activeCountry } = useActiveCountry();
  const { profile } = useSession();
  // Local (non-USD) cajas resolve to the active country's home currency.
  // Categoria_MDP is the source of truth: 'Internacional' → USD, 'Local'
  // → home currency. Replaced the AR-only ARS_CAJAS/USD_CAJAS lists
  // that used to live here — those returned 'UNKNOWN' for every MX
  // caja, which silently disabled the cross-currency UI (FX fields
  // never appeared on USD↔MXN transfers).
  const homeCurrency = activeCountry.code === 'MX' ? 'MXN' : 'ARS';
  const currencyByMedio = useMemo(() => {
    const m = new Map<string, 'ARS' | 'MXN' | 'USD'>();
    for (const medio of medios) {
      const name = medio.fields.Medio_MDP?.trim();
      if (!name) continue;
      m.set(name, medio.fields.Categoria_MDP === 'Internacional' ? 'USD' : homeCurrency);
    }
    return m;
  }, [medios, homeCurrency]);
  const currencyOf = (caja: string): 'ARS' | 'MXN' | 'USD' | 'UNKNOWN' =>
    currencyByMedio.get(caja) ?? 'UNKNOWN';

  const [destino, setDestino] = useState<string>('');
  const [monto, setMonto] = useState<string>('');
  const [tipoCambio, setTipoCambio] = useState<string>('');
  const [montoDestino, setMontoDestino] = useState<string>('');
  const [descripcion, setDescripcion] = useState<string>('');

  // Cross-month "Disponible" for the hint next to the Monto label —
  // matches PowerApps's `lbl_DepositoMontoDisponible_T` (3612).
  // `gcTime: 0` + `refetchOnMount: 'always'` mirrors the egreso dialog
  // fix (2026-05-08): never display a stale cached saldo on dialog
  // open — a concurrent Cierre de Caja can leave a transient
  // negative value in the cache.
  const saldoQ = useQuery({
    queryKey: ['tesoreria', 'saldo', origen],
    queryFn: () => getCajaSaldo(origen),
    gcTime: 0,
    refetchOnMount: 'always',
  });
  const saldoOrigen = saldoQ.data?.saldo ?? 0;
  const saldoLoading = saldoQ.isPending || saldoQ.isFetching;

  // Offer every active medio except the one we're transferring FROM, and
  // drop destinos the current user can't write to under the active
  // country (MX Nova → Nova cajas only; MX administracion → everything
  // except Nova cajas + Caja Diferencia; AR unchanged). Without this
  // pre-filter, an admin could pick Caja Diferencia as a destination
  // and the per-caja gate on `POST /tesoreria/transferencia` would 403
  // after the user filled the entire form.
  const grouped = useMemo(() => {
    const active = medios.filter(
      (m) =>
        m.fields.Status_MDP === 'Activo' &&
        m.fields.Medio_MDP !== origen &&
        canWriteCaja(m.fields.Medio_MDP, profile?.perfil, activeCountry.code),
    );
    const local = active.filter((m) => m.fields.Categoria_MDP === 'Local');
    const intl = active.filter((m) => m.fields.Categoria_MDP === 'Internacional');
    return { local, intl };
  }, [medios, origen, profile?.perfil, activeCountry.code]);

  const destinoCurrency = destino ? currencyOf(destino) : 'UNKNOWN';
  const crossCurrency =
    destinoCurrency !== 'UNKNOWN' && destinoCurrency !== origenCurrency;

  // Auto-compute montoDestino = monto × tipoCambio (or inverse). User
  // can still override the result. Convention: `tipoCambio` is always
  // entered as "home currency per USD" (e.g. 1000 ARS / 18 MXN per USD),
  // so when origen is USD we multiply and when it's the home currency
  // we divide. Previously this checked `destinoCurrency === 'ARS'`
  // explicitly, which made the multiply branch dead on MX (destino was
  // MXN there).
  useEffect(() => {
    if (!crossCurrency) return;
    const m = parseMoney(monto);
    const tc = parseMoney(tipoCambio);
    if (!m || !tc) return;
    const destCalc = origenCurrency === 'USD' ? m * tc : m / tc;
    setMontoDestino(formatDecimal(destCalc));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monto, tipoCambio, crossCurrency, origenCurrency, destinoCurrency]);

  const montoNum = parseMoney(monto) ?? 0;
  const tipoCambioNum = parseMoney(tipoCambio) ?? 0;
  const montoDestinoNum = parseMoney(montoDestino) ?? 0;

  const validationError = useMemo<string | null>(() => {
    if (!destino) return 'Seleccioná la caja destino.';
    if (destino === origen) return 'El origen y destino deben ser distintos.';
    if (montoNum <= 0) return 'Ingresá un monto mayor a cero.';
    if (montoNum > saldoOrigen + 0.01)
      return `El monto excede el saldo disponible (${formatMoney(saldoOrigen, origenCurrency)}).`;
    if (crossCurrency) {
      if (tipoCambioNum <= 0) return 'Ingresá el tipo de cambio.';
      if (montoDestinoNum <= 0) return 'Ingresá el monto en la cuenta destino.';
    }
    return null;
  }, [
    destino,
    origen,
    montoNum,
    saldoOrigen,
    origenCurrency,
    crossCurrency,
    tipoCambioNum,
    montoDestinoNum,
  ]);

  const mutation = useMutation({
    mutationFn: () =>
      crearTransferencia({
        kind,
        origen,
        destino,
        monto: montoNum,
        tipoCambio: crossCurrency ? tipoCambioNum : undefined,
        montoDestino: crossCurrency ? montoDestinoNum : undefined,
        observaciones: descripcion.trim() || undefined,
      }),
    onSuccess: (r) => {
      if (r.outcome === 'created') {
        const taxNote =
          r.impuestoRows > 0 ? ` · ${r.impuestoRows} impuesto(s) aplicado(s)` : '';
        toast.success(`${kind === 'Transferencia' ? 'Transferencia' : 'Depósito'} registrado${taxNote}`);
        qc.invalidateQueries({ queryKey: ['tesoreria'] });
        onClose();
      } else {
        toast.warning('No se pudo registrar', { description: r.message });
      }
    },
    onError: (err) =>
      toast.error('No se pudo registrar la operación', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      }),
  });

  const canSubmit = validationError === null && !mutation.isPending;
  const title =
    kind === 'Transferencia' ? 'Transferencia entre cuentas' : 'Depósito entre cuentas';

  return (
    <Dialog open onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent
        className="max-w-2xl"
        processing={mutation.isPending && (kind === 'Deposito' ? 'Registrando depósito…' : 'Registrando transferencia…')}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="trf-destino">Caja destino</Label>
              <Select value={destino} onValueChange={setDestino}>
                <SelectTrigger id="trf-destino" variant="filled">
                  <SelectValue placeholder="Seleccionar…" />
                </SelectTrigger>
                <SelectContent>
                  {grouped.local.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>Pesos</SelectLabel>
                      {grouped.local.map((m) => (
                        <SelectItem
  key={m.id}
  value={m.fields.Medio_MDP ?? ''}
  trailing={<LlevaFacturaTag show={m.fields.LlevaFactura_MDP === 'SI'} />}
>
  <CajaNombre caja={m.fields.Medio_MDP} tipo={m.fields.Tipo_MDP} />
</SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                  {grouped.intl.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>Dólares</SelectLabel>
                      {grouped.intl.map((m) => (
                        <SelectItem
  key={m.id}
  value={m.fields.Medio_MDP ?? ''}
  trailing={<LlevaFacturaTag show={m.fields.LlevaFactura_MDP === 'SI'} />}
>
  <CajaNombre caja={m.fields.Medio_MDP} tipo={m.fields.Tipo_MDP} />
</SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              {/* Plain `<Label>` (matching Caja destino) so the input
                  below sits at the same vertical offset as the Select
                  in the left column. The "Disponible" hint moved under
                  the input — mirror of the fix in AgregarMovimientoDialog
                  where stacking the hint above pushed the field down. */}
              <Label htmlFor="trf-monto">Monto ({origenCurrency})</Label>
              <MoneyInput
                id="trf-monto"
                variant="filled"
                placeholder="Ingresar monto"
                value={monto}
                moneda={origenCurrency}
                onChange={setMonto}
                autoFocus
              />
              <p className="flex items-center gap-1 text-xs font-medium text-rose-700">
                {saldoLoading ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                    <span>Calculando saldo…</span>
                  </>
                ) : (
                  <>Disponible: {formatMoney(saldoOrigen, origenCurrency)}</>
                )}
              </p>
            </div>
          </div>

          {crossCurrency && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="trf-tc">Tipo de cambio</Label>
                <Input
                  id="trf-tc"
                  variant="filled"
                  type="text"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={tipoCambio}
                  onChange={(e) => setTipoCambio(sanitizeDecimal(e.target.value))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="trf-monto-dest">
                  Monto en destino ({destinoCurrency})
                </Label>
                <MoneyInput
                  id="trf-monto-dest"
                  variant="filled"
                  placeholder="0,00"
                  value={montoDestino}
                  // crossCurrency guards this branch — destinoCurrency
                  // is narrowed to 'ARS' | 'MXN' | 'USD' here, never 'UNKNOWN'.
                  moneda={destinoCurrency}
                  onChange={setMontoDestino}
                />
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="trf-desc">Descripción</Label>
            <Textarea
              id="trf-desc"
              variant="filled"
              placeholder="Ingresar observación"
              rows={4}
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
            />
          </div>

          {kind === 'Deposito' && destino === 'Transferencia Galicia' && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Los depósitos a Galicia generan automáticamente los impuestos Ley 25.413 y
              SIRCREB (cuando corresponde).
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={!canSubmit}
            title={validationError ?? undefined}
            className="gap-2 bg-foreground text-background hover:bg-foreground/90"
          >
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function sanitizeDecimal(value: string): string {
  const cleaned = value.replace(/[^\d.,]/g, '');
  const match = cleaned.match(/^\d*([.,]\d*)?/);
  return match ? match[0] : '';
}

function formatDecimal(n: number): string {
  return String(Math.round(n * 100) / 100).replace('.', ',');
}
